import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiEdit2 } from 'react-icons/fi';

const AUTOSAVE_DELAY_MS = 600;

const normalizeValue = (value) => (
  value === null || value === undefined ? '' : String(value)
);

const parseValue = (rawValue, { allowEmpty, integer, fieldLabel }) => {
  const trimmed = String(rawValue ?? '').trim();
  if (!trimmed && allowEmpty) return { value: null, signature: '' };
  const parsed = Number(trimmed);
  if (!trimmed || !Number.isFinite(parsed) || parsed < 0 || (integer && !Number.isInteger(parsed))) {
    return {
      error: `Ingresa un ${fieldLabel} ${integer ? 'entero ' : ''}válido.`,
    };
  }
  return { value: parsed, signature: String(parsed) };
};

const InlineQuickPriceInput = ({
  formatId,
  value,
  disabled = false,
  saving = false,
  onCommit,
  allowEmpty = false,
  integer = true,
  fieldLabel = 'precio',
  ariaLabel = 'Precio final',
  inputIdPrefix = 'quick-price',
  placeholder,
  resetSignal,
  onDraftChange,
  onDirtyChange,
  onDisplayedValueChange,
  debounceMs = AUTOSAVE_DELAY_MS,
  autoSave = true,
  requireEditActivation = false,
  editButtonAriaLabel,
  editButtonAriaDescribedBy,
  editButtonTitle,
  saveButtonAriaLabel,
  onEditRequest,
  trailingAction,
  adjacentAction,
}) => {
  const [draft, setDraft] = useState(normalizeValue(value));
  const [validationError, setValidationError] = useState('');
  const [internallySaving, setInternallySaving] = useState(false);
  const [editEnabled, setEditEnabled] = useState(!requireEditActivation);
  const draftRef = useRef(normalizeValue(value));
  const serverValueRef = useRef(normalizeValue(value));
  const lastSubmittedRef = useRef(null);
  const queuedValueRef = useRef(null);
  const activeCommitRef = useRef(false);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);
  const onCommitRef = useRef(onCommit);
  const onDirtyChangeRef = useRef(onDirtyChange);
  const onDisplayedValueChangeRef = useRef(onDisplayedValueChange);
  const savingRef = useRef(saving);
  const commitValueRef = useRef(null);
  const inputRef = useRef(null);
  const editButtonRef = useRef(null);
  const focusButtonAfterSaveRef = useRef(false);
  const skipNextBlurCommitRef = useRef(false);
  const wasDisabledRef = useRef(disabled);
  onCommitRef.current = onCommit;
  onDirtyChangeRef.current = onDirtyChange;
  onDisplayedValueChangeRef.current = onDisplayedValueChange;
  savingRef.current = saving;

  const updateDraft = (nextValue) => {
    draftRef.current = nextValue;
    setDraft(nextValue);
  };

  const clearTimer = () => {
    if (!timerRef.current) return;
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const reportDirty = (rawValue) => {
    const parsed = parseValue(rawValue, { allowEmpty, integer, fieldLabel });
    const server = parseValue(serverValueRef.current, { allowEmpty, integer, fieldLabel });
    onDirtyChangeRef.current?.(Boolean(
      parsed.error || server.error || parsed.signature !== server.signature
    ));
  };

  const commitValue = async (rawValue) => {
    clearTimer();
    const parsed = parseValue(rawValue, { allowEmpty, integer, fieldLabel });
    if (parsed.error) {
      if (mountedRef.current) setValidationError(parsed.error);
      return false;
    }
    if (savingRef.current && !activeCommitRef.current) {
      queuedValueRef.current = { rawValue, signature: parsed.signature };
      return true;
    }
    const server = parseValue(serverValueRef.current, { allowEmpty, integer, fieldLabel });
    if (!server.error && parsed.signature === server.signature && !activeCommitRef.current) {
      if (mountedRef.current) setValidationError('');
      if (mountedRef.current) reportDirty(rawValue);
      return true;
    }
    if (activeCommitRef.current) {
      queuedValueRef.current = { rawValue, signature: parsed.signature };
      return true;
    }

    activeCommitRef.current = true;
    lastSubmittedRef.current = parsed.signature;
    if (mountedRef.current) {
      setValidationError('');
      setInternallySaving(true);
    }
    let succeeded = false;
    try {
      const result = await onCommitRef.current(parsed.value);
      succeeded = result !== false;
      if (succeeded) {
        serverValueRef.current = normalizeValue(parsed.value);
        lastSubmittedRef.current = null;
        if (mountedRef.current) reportDirty(draftRef.current);
      }
      return result;
    } finally {
      if (!succeeded && lastSubmittedRef.current === parsed.signature) {
        lastSubmittedRef.current = null;
      }
      activeCommitRef.current = false;
      const queued = queuedValueRef.current;
      queuedValueRef.current = null;
      if (mountedRef.current) setInternallySaving(false);
      if (mountedRef.current && queued && (!succeeded || queued.signature !== parsed.signature)) {
        void commitValue(queued.rawValue);
      }
    }
  };
  commitValueRef.current = commitValue;

  const scheduleCommit = () => {
    if (!autoSave) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void commitValue(draftRef.current);
    }, debounceMs);
  };

  useEffect(() => {
    onDisplayedValueChangeRef.current?.(draft);
  }, [draft]);

  useEffect(() => {
    const nextServerValue = normalizeValue(value);
    const previousServerValue = serverValueRef.current;
    serverValueRef.current = nextServerValue;
    const currentDraft = draftRef.current;
    const parsedCurrent = parseValue(currentDraft, { allowEmpty, integer, fieldLabel });
    const parsedNext = parseValue(nextServerValue, { allowEmpty, integer, fieldLabel });
    const incomingAcknowledgesSubmission = lastSubmittedRef.current !== null
      && !parsedCurrent.error
      && !parsedNext.error
      && parsedCurrent.signature === lastSubmittedRef.current
      && parsedNext.signature === lastSubmittedRef.current;
    const mayFollowServer = currentDraft === previousServerValue
      || incomingAcknowledgesSubmission;
    if (mayFollowServer) {
      updateDraft(nextServerValue);
      setValidationError('');
      onDirtyChangeRef.current?.(false);
      if (incomingAcknowledgesSubmission) lastSubmittedRef.current = null;
    }
  }, [allowEmpty, fieldLabel, integer, value]);

  useEffect(() => {
    if (resetSignal === undefined) return;
    clearTimer();
    queuedValueRef.current = null;
    updateDraft(serverValueRef.current);
    setValidationError('');
    onDirtyChangeRef.current?.(false);
    if (requireEditActivation) setEditEnabled(false);
  }, [requireEditActivation, resetSignal]);

  useEffect(() => {
    const wasDisabled = wasDisabledRef.current;
    wasDisabledRef.current = disabled;
    if (disabled) {
      clearTimer();
      return;
    }
    if (!wasDisabled) return;
    const parsed = parseValue(draftRef.current, { allowEmpty, integer, fieldLabel });
    const server = parseValue(serverValueRef.current, { allowEmpty, integer, fieldLabel });
    if (!autoSave || parsed.error || server.error || parsed.signature === server.signature) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void commitValueRef.current(draftRef.current);
    }, debounceMs);
  }, [allowEmpty, autoSave, debounceMs, disabled, fieldLabel, integer]);

  useEffect(() => {
    if (!requireEditActivation || !editEnabled || disabled) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [disabled, editEnabled, requireEditActivation]);

  useEffect(() => {
    if (disabled && requireEditActivation) setEditEnabled(false);
  }, [disabled, requireEditActivation]);

  useEffect(() => {
    if (saving || activeCommitRef.current || !queuedValueRef.current) return;
    const queued = queuedValueRef.current;
    queuedValueRef.current = null;
    void commitValueRef.current(queued.rawValue);
  }, [saving]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      queuedValueRef.current = null;
      clearTimer();
      onDirtyChangeRef.current?.(false);
    };
  }, []);

  const busy = saving || internallySaving;
  const inputId = `${inputIdPrefix}-${formatId}`;
  const delegatesEdit = requireEditActivation && typeof onEditRequest === 'function';
  const protectedReadOnly = requireEditActivation && (delegatesEdit || !editEnabled);
  const resolvedTrailingAction = !requireEditActivation ? trailingAction : null;
  const hasAttachedAction = requireEditActivation || Boolean(resolvedTrailingAction);
  const actionButtonLabel = editEnabled && !delegatesEdit
    ? saveButtonAriaLabel || `Guardar ${fieldLabel}`
    : editButtonAriaLabel || `Editar ${fieldLabel}`;

  useEffect(() => {
    if (!focusButtonAfterSaveRef.current || busy || disabled || editEnabled) return;
    focusButtonAfterSaveRef.current = false;
    if (document.activeElement === inputRef.current) {
      skipNextBlurCommitRef.current = true;
    }
    editButtonRef.current?.focus();
  }, [busy, disabled, editEnabled]);

  const lockProtectedEditor = ({ focusButton = false } = {}) => {
    if (!requireEditActivation) return;
    focusButtonAfterSaveRef.current = focusButton;
    setEditEnabled(false);
  };

  const commitAndMaybeLock = async ({ focusButton = false } = {}) => {
    try {
      const saved = await commitValue(draftRef.current);
      const latestDraft = parseValue(draftRef.current, { allowEmpty, integer, fieldLabel });
      const savedValue = parseValue(serverValueRef.current, { allowEmpty, integer, fieldLabel });
      if (saved !== false && mountedRef.current
        && !activeCommitRef.current && !queuedValueRef.current
        && !latestDraft.error && !savedValue.error
        && latestDraft.signature === savedValue.signature) {
        lockProtectedEditor({ focusButton });
      }
    } catch (_error) {
      // The parent owns persistence errors. Keep the guarded field open for correction/retry.
    }
  };

  return (
    <div className="min-w-0">
      <div className="flex items-start gap-1">
      <div className="relative w-28">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5 text-xs text-slate-500">
          $
        </span>
        <input
          ref={inputRef}
          id={inputId}
          aria-label={ariaLabel}
          aria-invalid={Boolean(validationError)}
          aria-describedby={validationError ? `${inputId}-error` : undefined}
          aria-busy={busy}
          type="number"
          inputMode={integer ? 'numeric' : 'decimal'}
          min="0"
          step={integer ? '1' : 'any'}
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={protectedReadOnly}
          tabIndex={protectedReadOnly ? -1 : undefined}
          onChange={(event) => {
            if (protectedReadOnly) return;
            updateDraft(event.target.value);
            if (validationError) setValidationError('');
            onDraftChange?.(event.target.value);
            reportDirty(event.target.value);
            scheduleCommit();
          }}
          onBlur={(event) => {
            if (skipNextBlurCommitRef.current) {
              skipNextBlurCommitRef.current = false;
              return;
            }
            if (requireEditActivation) {
              if (event.relatedTarget === editButtonRef.current) return;
              if (!autoSave) return;
              void commitAndMaybeLock();
              return;
            }
            if (!autoSave) return;
            void commitValue(draftRef.current);
          }}
          onKeyDown={(event) => {
            if (protectedReadOnly) return;
            if (event.key === 'Escape') {
              event.preventDefault();
              clearTimer();
              const restoredValue = serverValueRef.current;
              const restored = parseValue(restoredValue, { allowEmpty, integer, fieldLabel });
              queuedValueRef.current = (activeCommitRef.current || savingRef.current) && !restored.error
                ? { rawValue: restoredValue, signature: restored.signature }
                : null;
              updateDraft(restoredValue);
              setValidationError('');
              onDraftChange?.(restoredValue);
              reportDirty(restoredValue);
              lockProtectedEditor({ focusButton: true });
              return;
            }
            if (event.key !== 'Enter') return;
            event.preventDefault();
            if (requireEditActivation) {
              void commitAndMaybeLock({ focusButton: true });
              return;
            }
            void commitValue(draftRef.current);
          }}
          className={`no-number-spinner w-full rounded-md border py-1.5 pl-6 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 disabled:cursor-not-allowed disabled:bg-slate-100 ${
            hasAttachedAction ? 'pr-9' : 'pr-7'
          } ${protectedReadOnly ? 'cursor-default bg-slate-50 text-slate-700' : 'bg-white'} ${
            validationError
              ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
              : 'border-slate-300 focus:border-blue-500 focus:ring-blue-100'
          }`}
        />
        {busy && (
          <span
            role="status"
            aria-label={`Guardando ${fieldLabel}`}
            className={`absolute inset-y-0 flex items-center text-blue-600 ${
              hasAttachedAction ? 'right-9' : 'right-2'
            }`}
          >
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
          </span>
        )}
        {requireEditActivation && (
          <button
            ref={editButtonRef}
            type="button"
            aria-label={actionButtonLabel}
            aria-describedby={editButtonAriaDescribedBy}
            aria-controls={inputId}
            title={editButtonTitle || actionButtonLabel}
            disabled={disabled || busy}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              if (disabled || savingRef.current || activeCommitRef.current) return;
              if (delegatesEdit) {
                onEditRequest();
                return;
              }
              if (editEnabled) {
                void commitAndMaybeLock({ focusButton: true });
                return;
              }
              setEditEnabled(true);
            }}
            className={`absolute inset-y-px right-px z-10 inline-flex w-8 items-center justify-center rounded-r-[5px] border-l border-slate-300 outline-none transition focus:ring-2 focus:ring-inset focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${
              editEnabled
                ? 'bg-blue-50 text-blue-700'
                : 'bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-blue-700'
            }`}
          >
            {editEnabled && !delegatesEdit
              ? <FiCheck aria-hidden="true" className="h-3.5 w-3.5" />
              : <FiEdit2 aria-hidden="true" className="h-3.5 w-3.5" />}
          </button>
        )}
        {resolvedTrailingAction && (
          <button
            type="button"
            aria-label={resolvedTrailingAction.ariaLabel}
            aria-controls={inputId}
            aria-busy={resolvedTrailingAction.ariaBusy || undefined}
            title={resolvedTrailingAction.title || resolvedTrailingAction.ariaLabel}
            disabled={busy
              || (disabled && !resolvedTrailingAction.allowWhenInputDisabled)
              || resolvedTrailingAction.disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => resolvedTrailingAction.onClick?.()}
            className={`absolute inset-y-px right-px z-10 inline-flex w-8 items-center justify-center rounded-r-[5px] border-l border-slate-300 outline-none transition focus:ring-2 focus:ring-inset focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 ${resolvedTrailingAction.pending ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-slate-50 text-slate-500 hover:bg-blue-50 hover:text-blue-700'}`}
          >
            {resolvedTrailingAction.icon}
          </button>
        )}
      </div>
      {adjacentAction}
      </div>
      {validationError && (
        <p id={`${inputId}-error`} className="mt-1 max-w-32 text-[11px] font-medium leading-tight text-red-600">
          {validationError}
        </p>
      )}
    </div>
  );
};

export default InlineQuickPriceInput;
